import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Breadcrumb from '../common/Breadcrumb';
import starLogo from '../../assets/star-logo.svg';
import '../../styles/layout/AppLayout.css';

interface AppLayoutProps {
  children: React.ReactNode;
}

/**
 * AppLayout component provides the main layout structure for authenticated pages
 * including navigation, header, and footer
 */
const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const { user, signOut } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    // Toggle a class on body to prevent scrolling when menu is open
    document.body.classList.toggle('nav-menu-open');
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Navigation menu items
  const navItems = [
    { path: '/app', label: 'Home', icon: '🏠' },
    { path: '/create-story', label: 'Create Story', icon: '✨' },
    { path: '/my-stories', label: 'My Stories', icon: '📚' },
    { path: '/workshop', label: 'Story Workshop', icon: '🧰' },
    { path: '/profile', label: 'Profile', icon: '👤' },
    { path: '/settings', label: 'Settings', icon: '⚙️' }
  ];

  return (
    <div className="app-layout">
      {/* Mobile Menu Button (now floating) */}
      <button 
        className="mobile-menu-button"
        onClick={toggleMobileMenu}
        aria-label="Toggle menu"
        aria-expanded={mobileMenuOpen}
      >
        {mobileMenuOpen ? '✕' : '☰'}
      </button>
      
      {/* Main Navigation - TOP MENU BAR */}
      <div className="main-navigation">
        <nav className="main-nav-container">
          {/* App Logo now in the navbar */}
          <div className="app-logo">
            <Link to="/app">
              <img src={starLogo} alt="Wisporia Logo" />
              <span>Wisporia</span>
            </Link>
          </div>
          
          {/* User Info now in the navbar */}
          <div className="user-info desktop-only">
            <div className="user-avatar">
              {user?.email?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="user-name">
              {user?.email?.split('@')[0] || 'User'}
            </div>
          </div>
        </nav>
      </div>
      
      {/* Mobile Navigation Menu */}
      <div className={`mobile-nav ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-nav-header">
          <div className="user-info">
            <div className="user-avatar">
              {user?.email?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="user-details">
              <div className="user-name">
                {user?.email?.split('@')[0] || 'User'}
              </div>
              <div className="user-email">{user?.email}</div>
            </div>
          </div>
        </div>
        
        <nav className="mobile-nav-menu">
          <ul className="mobile-nav-list">
            {navItems.map((item) => (
              <li key={item.path} className="mobile-nav-item">
                <NavLink 
                  to={item.path} 
                  className={({ isActive }) => 
                    isActive ? 'mobile-nav-link active' : 'mobile-nav-link'
                  }
                >
                  <span className="mobile-nav-icon">{item.icon}</span>
                  <span className="mobile-nav-text">{item.label}</span>
                </NavLink>
              </li>
            ))}
            <li className="mobile-nav-item">
              <button onClick={handleSignOut} className="mobile-nav-link logout-button">
                <span className="mobile-nav-icon">🚪</span>
                <span className="mobile-nav-text">Logout</span>
              </button>
            </li>
          </ul>
        </nav>
        
        <div className="mobile-nav-footer">
          <Link to="/" className="back-to-home">
            ← Back to Home Page
          </Link>
        </div>
      </div>
      
      {/* Main Content */}
      <main className="app-content">
        <Breadcrumb />
        {children}
      </main>
      
      {/* App Footer */}
      <footer className="app-footer">
        <p>&copy; {new Date().getFullYear()} Wisporia. All rights reserved.</p>
      </footer>
      
      {/* Overlay for mobile menu */}
      {mobileMenuOpen && (
        <div 
          className="mobile-nav-overlay" 
          onClick={toggleMobileMenu}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

export default AppLayout; 